import React, { useEffect } from 'react';

interface CoreWebVitalsOptimizerProps {
  pageType?: string;
  entityData?: any;
  optimizations?: {
    preloadCriticalResources?: boolean;
    optimizeImages?: boolean;
    prefetchRoutes?: boolean;
    enableResourceHints?: boolean;
  };
}

const CoreWebVitalsOptimizer: React.FC<CoreWebVitalsOptimizerProps> = ({
  pageType = 'static',
  entityData,
  optimizations = {
    preloadCriticalResources: true,
    optimizeImages: true,
    prefetchRoutes: true,
    enableResourceHints: true
  }
}) => {
  useEffect(() => {
    // Optimize Largest Contentful Paint (LCP)
    if (optimizations.preloadCriticalResources) {
      const criticalResources = [
        '/fonts/inter-var.woff2',
        '/images/hero-bg.webp'
      ];
      
      criticalResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource;
        link.as = resource.includes('.woff') ? 'font' : 'image';
        if (resource.includes('.woff')) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      });
    }

    // Optimize First Input Delay (FID)
    if (optimizations.prefetchRoutes) {
      const commonRoutes = [
        '/find',
        '/therapists',
        '/about'
      ];
      
      commonRoutes.forEach(route => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = route;
        document.head.appendChild(link);
      });
    }

    // Optimize Cumulative Layout Shift (CLS)
    if (optimizations.optimizeImages) {
      const images = document.querySelectorAll('img[data-optimize]');
      images.forEach(img => {
        if (!img.getAttribute('width') || !img.getAttribute('height')) {
          img.setAttribute('style', 'aspect-ratio: 16/9; object-fit: cover;');
        }
      });
    }

    // Resource hints for external domains
    if (optimizations.enableResourceHints) {
      const resourceHints = [
        { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
        { rel: 'dns-prefetch', href: '//www.google-analytics.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
      ];

      resourceHints.forEach(hint => {
        const link = document.createElement('link');
        link.rel = hint.rel;
        link.href = hint.href;
        if (hint.crossOrigin) {
          link.crossOrigin = hint.crossOrigin;
        }
        document.head.appendChild(link);
      });
    }
  }, [optimizations]);

  // Performance observer for monitoring
  useEffect(() => {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          // Log Core Web Vitals metrics
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
          }
          if (entry.entryType === 'first-input') {
            console.log('FID:', (entry as any).processingStart - entry.startTime);
          }
          if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
            console.log('CLS:', (entry as any).value);
          }
        });
      });

      observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });

      return () => observer.disconnect();
    }
  }, []);

  return (
    <>
      {/* Critical CSS for above-the-fold content */}
      <style>{`
        .hero-section {
          contain: layout style paint;
        }
        
        .lazy-load {
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        
        .lazy-load.loaded {
          opacity: 1;
        }
        
        /* Optimize font loading */
        @font-face {
          font-family: 'Inter';
          font-display: swap;
          src: url('/fonts/inter-var.woff2') format('woff2');
        }
      `}</style>

      {/* Page-specific optimizations */}
      {pageType === 'city' && entityData && (
        <>
          <link
            rel="preload"
            href={`/images/cities/${entityData.slug}.webp`}
            as="image"
            type="image/webp"
          />
          <link
            rel="prefetch"
            href={`/api/therapists/${entityData.slug}`}
          />
        </>
      )}

      {/* Service Worker registration for caching */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            if ('serviceWorker' in navigator) {
              window.addEventListener('load', () => {
                navigator.serviceWorker.register('/sw.js')
                  .then(registration => console.log('SW registered'))
                  .catch(error => console.log('SW registration failed'));
              });
            }
          `
        }}
      />
    </>
  );
};

export default CoreWebVitalsOptimizer;